import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyGallery } from '../../interfaces/Gallery/Gallery';
import {
  createGallery,
  deleteGallery,
  getGallery,
  updateGallery,
  updateGalleryMedia,
} from '../../api/Gallery';
import { GalleryMediaOptions } from '../../interfaces/Gallery/GalleryMediaOptions';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { ImageInput } from '../../components/ImageInput/ImageInput';

export const ModifyGalleryPage = () => {
  const navigate = useNavigate();

  const { galleryID: galleryIDAsStr } = useParams<string>();

  const galleryID = galleryIDAsStr ? Number(galleryIDAsStr) : undefined;
  const isEditing = Number.isFinite(galleryID);

  const [gallery, setGallery] = useState(getEmptyGallery());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshGallery = () => {
    setLoading(true);
    getGallery(galleryID!)
      .then((g) => setGallery(g))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshGallery();
    }
  }, [galleryIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateGallery(gallery)
        .then((updatedGallery) => {
          toast.success('Galeri başarıyla güncellendi');
          setGallery(updatedGallery);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createGallery(gallery)
        .then((createdGallery) => {
          toast.success('Galeri başarıyla oluşturuldu');
          navigate(`/visual/galleries/${createdGallery.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleUpdateMedia = (options: GalleryMediaOptions) => {
    setLoading(true);
    updateGalleryMedia(gallery.id, options)
      .then((updatedGallery) => {
        toast.success('Medya başarıyla güncellendi');
        setGallery(updatedGallery);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteGallery(gallery.id)
      .then(() => {
        toast.success('Galeri başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/visual/galleries');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili galeriyi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Galeriyi Düzenle' : 'Yeni Galeri'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='Link'
          variant='outlined'
          size='small'
          value={gallery.linkUrl}
          onChange={(e) => setGallery({ ...gallery, linkUrl: e.target.value })}
          fullWidth
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            mt: 1.5,
            gap: 1.5,
          }}
        >
          <ImageInput
            title='Image'
            existingImage={gallery.image}
            onUpload={(file) => handleUpdateMedia({ image: file })}
            onDelete={() => handleUpdateMedia({ eraseImage: true })}
          />
        </Box>
      )}
    </Box>
  );
};
